import { BulkBookingFrequency, BulkBookingWeekOrdinal, RecurrencePattern } from './Types';

/**
 * Weekdays in the order the picker presents them. `value` is what the server parses into a
 * `DayOfWeek`, so it must stay a day name.
 */
export const DAY_OPTIONS: { value: string; label: string; name: string }[] = [
	{ value: 'monday', label: 'M', name: 'Monday' },
	{ value: 'tuesday', label: 'T', name: 'Tuesday' },
	{ value: 'wednesday', label: 'W', name: 'Wednesday' },
	{ value: 'thursday', label: 'Th', name: 'Thursday' },
	{ value: 'friday', label: 'F', name: 'Friday' },
	{ value: 'saturday', label: 'S', name: 'Saturday' },
	{ value: 'sunday', label: 'Su', name: 'Sunday' },
];

/** Occurrences within a month, in calendar order. `value` is parsed server-side by name. */
export const WEEK_ORDINAL_OPTIONS: { value: BulkBookingWeekOrdinal; label: string; name: string }[] = [
	{ value: 'first', label: '1st', name: 'first' },
	{ value: 'second', label: '2nd', name: 'second' },
	{ value: 'third', label: '3rd', name: 'third' },
	{ value: 'fourth', label: '4th', name: 'fourth' },
	{ value: 'last', label: 'Last', name: 'last' },
];

export const FREQUENCY_OPTIONS: { value: BulkBookingFrequency; label: string }[] = [
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'monthly', label: 'Monthly' },
];

/** The noun the interval counts, singular or plural to match. */
export function intervalUnitLabel(frequency: BulkBookingFrequency, interval: number): string {
	const unit = frequency === 'monthly' ? 'month' : 'week';
	return interval === 1 ? unit : `${unit}s`;
}

/** "a", "a and b", "a, b and c". */
function formatList(items: string[]): string {
	if (items.length <= 1) {
		return items[0] ?? '';
	}
	return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The longest span one search may cover. Mirrors `BulkBookingRecurrence.MaxSearchSpanDays` — the
 * server rejects anything longer, so this exists only to say so as a field error rather than a
 * failed request.
 */
export const MAX_SEARCH_SPAN_DAYS = 550;

/** Whole days from `start` to `end` inclusive of neither endpoint's time of day. */
export function searchSpanDays(start: Date, end: Date): number {
	const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
	return Math.round((startOfDay(end) - startOfDay(start)) / 86_400_000);
}

/** Clamp a possibly-empty or nonsensical interval to something the server will accept. */
export function normaliseInterval(interval: number | undefined): number {
	if (!Number.isFinite(interval) || (interval as number) < 1) {
		return 1;
	}
	return Math.floor(interval as number);
}

/**
 * A plain-English restatement of the pattern, shown under the inputs so the user can confirm what
 * they picked before the search runs. Returns null when the pattern is not yet complete enough to
 * describe.
 */
export function describeRecurrence(state: RecurrencePattern): string | null {
	const days = DAY_OPTIONS.filter(day => state.daysOfWeek.includes(day.value)).map(day => day.name);
	if (days.length === 0) {
		return null;
	}

	const interval = normaliseInterval(state.interval);

	if (state.frequency === 'monthly') {
		const ordinals = WEEK_ORDINAL_OPTIONS.filter(ordinal => state.weekOrdinals.includes(ordinal.value)).map(
			ordinal => ordinal.name,
		);
		if (ordinals.length === 0) {
			return null;
		}

		const every = interval === 1 ? 'every month' : `every ${interval} months`;
		return `The ${formatList(ordinals)} ${formatList(days)} of ${every}`;
	}

	const every = interval === 1 ? 'Every week' : `Every ${interval} weeks`;
	return `${every} on ${formatList(days)}`;
}
